import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '../../context/UserContext';
import { useModelCatalog, BOOTSTRAP_MODEL_ID } from '../../hooks/useModelCatalog';
import { DEFAULT_MAX_TOKENS } from '../../utils/chatSettings';

/**
 * SettingsModal - Full settings modal with tabbed interface
 *
 * Tabs:
 * - Appearance: Theme, Font Size, Message Density
 * - Chat: Model defaults, Max tokens
 * - Custom Instructions: Global system prompts
 * - Privacy: Data and privacy settings
 */

const TABS = [
  { id: 'appearance', label: 'Appearance', icon: 'palette' },
  { id: 'chat', label: 'Chat', icon: 'chat' },
  { id: 'instructions', label: 'Custom Instructions', icon: 'document' },
  { id: 'privacy', label: 'Privacy', icon: 'shield' },
];

function SettingsModal({ isOpen, onClose }) {
  const { theme, setTheme } = useTheme();
  const { user, defaultModel, setDefaultModel } = useUser();
  const modalRef = useRef(null);
  // DOS-415 Phase 4b: the model list follows the catalog, not a literal.
  const { models, defaultModel: catalogDefault } = useModelCatalog();
  const [activeTab, setActiveTab] = useState('appearance');

  // Settings state (with localStorage persistence)
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('app-settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse settings:', e);
      }
    }
    return {
      // Appearance
      theme: theme,
      fontSize: 'medium',
      messageDensity: 'comfortable',
      codeTheme: 'auto',
      // Chat
      defaultModel: BOOTSTRAP_MODEL_ID,
      maxTokens: DEFAULT_MAX_TOKENS,
      streamResponses: true,
      // Custom Instructions
      customInstructions: '',
      // API
      apiKeyConfigured: false,
      // Privacy
      saveHistory: true,
      shareAnalytics: false,
    };
  });

  // Track unsaved changes
  const [hasChanges, setHasChanges] = useState(false);
  const [initialSettings, setInitialSettings] = useState(settings);
  // The model is saved to the account, not localStorage; like the other
  // settings it waits for Save. null: not changed.
  const [pendingModel, setPendingModel] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setInitialSettings(settings);
      setHasChanges(false);
      setPendingModel(null);
    }
  }, [isOpen]);

  // Store previously focused element for restoration
  const previouslyFocusedRef = useRef(null);

  // Handle escape key and focus trap (SAT-87)
  useEffect(() => {
    if (!isOpen) return;

    // Store the currently focused element
    previouslyFocusedRef.current = document.activeElement;

    // Focus the first focusable element in the modal
    const focusableSelector = 'button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const firstFocusable = modalRef.current?.querySelector(focusableSelector);
    setTimeout(() => firstFocusable?.focus(), 0);

    const handleKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      // Tab key - trap focus within modal
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = Array.from(
          modalRef.current.querySelectorAll(focusableSelector)
        ).filter(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && !el.disabled;
        });

        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: If on first element, wrap to last
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: If on last element, wrap to first
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus when modal closes
      previouslyFocusedRef.current?.focus();
    };
  }, [isOpen, hasChanges]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      // Revert to initial settings
      setSettings(initialSettings);
    }
    setPendingModel(null);
    onClose();
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const pickModel = (id) => {
    setPendingModel(id);
    setHasChanges(true);
  };

  const handleSave = () => {
    if (pendingModel && pendingModel !== defaultModel) setDefaultModel(pendingModel);
    setPendingModel(null);

    // Save to localStorage
    localStorage.setItem('app-settings', JSON.stringify(settings));

    // Apply theme immediately
    setTheme(settings.theme);

    // Apply other settings (font size, etc.)
    document.documentElement.dataset.fontSize = settings.fontSize;
    document.documentElement.dataset.density = settings.messageDensity;

    setHasChanges(false);
    setInitialSettings(settings);
    onClose();
  };

  if (!isOpen) return null;

  const renderTabIcon = (iconName) => {
    switch (iconName) {
      case 'palette':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
        );
      case 'chat':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      case 'document':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'key':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        );
      case 'shield':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'appearance':
        return <AppearanceTab settings={settings} updateSetting={updateSetting} />;
      case 'chat':
        return (
          <ChatTab
            settings={settings} updateSetting={updateSetting}
            model={pendingModel ?? defaultModel} pickModel={pickModel}
            models={models} catalogDefault={catalogDefault}
          />
        );
      case 'instructions':
        return <InstructionsTab settings={settings} updateSetting={updateSetting} />;
      case 'privacy':
        return <PrivacyTab settings={settings} updateSetting={updateSetting} />;
      default:
        return null;
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-backdrop-transition animate-fadeIn"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-modal-title"
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh]
          flex flex-col animate-fadeInScale gpu-accelerated"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="settings-modal-title" className="text-xl font-semibold text-gray-900 dark:text-white">
            Settings
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200
              transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Tab Navigation */}
          <nav className="w-48 shrink-0 border-r border-gray-200 dark:border-gray-700 py-4 overflow-y-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm font-medium
                  transition-colors ${
                    activeTab === tab.id
                      ? 'bg-decisiv-blue/10 text-decisiv-blue border-r-2 border-decisiv-blue'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                {renderTabIcon(tab.icon)}
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderTabContent()}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {hasChanges && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600
                rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="px-4 py-2 text-sm font-medium text-white
                bg-decisiv-blue hover:bg-decisiv-blue/90 disabled:opacity-50
                disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Appearance Tab Component
function AppearanceTab({ settings, updateSetting }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Appearance</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Customize the look and feel of the application.
        </p>
      </div>

      {/* Theme */}
      <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Theme</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Choose your preferred color scheme</p>
        </div>
        <select
          value={settings.theme}
          onChange={(e) => updateSetting('theme', e.target.value)}
          className="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
            rounded-lg px-4 py-2 text-gray-900 dark:text-white
            focus:ring-2 focus:ring-decisiv-blue/50 outline-hidden"
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="system">System</option>
        </select>
      </div>

      {/* Font Size */}
      <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Font Size</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Adjust text size throughout the app</p>
        </div>
        <select
          value={settings.fontSize}
          onChange={(e) => updateSetting('fontSize', e.target.value)}
          className="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
            rounded-lg px-4 py-2 text-gray-900 dark:text-white
            focus:ring-2 focus:ring-decisiv-blue/50 outline-hidden"
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
      </div>

      {/* Message Density */}
      <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Message Density</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Spacing between messages in chat</p>
        </div>
        <select
          value={settings.messageDensity}
          onChange={(e) => updateSetting('messageDensity', e.target.value)}
          className="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
            rounded-lg px-4 py-2 text-gray-900 dark:text-white
            focus:ring-2 focus:ring-decisiv-blue/50 outline-hidden"
        >
          <option value="compact">Compact</option>
          <option value="comfortable">Comfortable</option>
          <option value="spacious">Spacious</option>
        </select>
      </div>

      {/* Response Display Mode - DOS-202 */}
      <div className="flex items-center justify-between py-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Response Display</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">How long AI responses are shown</p>
        </div>
        <select
          value={settings.responseDisplayMode || 'summary'}
          onChange={(e) => updateSetting('responseDisplayMode', e.target.value)}
          className="bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
            rounded-lg px-4 py-2 text-gray-900 dark:text-white
            focus:ring-2 focus:ring-decisiv-blue/50 outline-hidden"
        >
          <option value="summary">Summary Mode</option>
          <option value="full">Full Report</option>
        </select>
      </div>
    </div>
  );
}

// Chat Tab Component
function ChatTab({ settings, updateSetting, model, pickModel, models, catalogDefault }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Chat Settings</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Configure default model settings and chat behavior.
        </p>
      </div>

      {/* Model — settable here only; new conversations use this value, and
          existing conversations stay locked to whatever model they were
          started with. Per-user, persisted to your account on Save. */}
      <div className="py-3 border-b border-gray-200 dark:border-gray-700">
        <label className="block font-medium text-gray-900 dark:text-white mb-2">Model</label>
        <select
          value={model || catalogDefault || BOOTSTRAP_MODEL_ID}
          onChange={(e) => pickModel(e.target.value)}
          className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
            rounded-lg px-4 py-2.5 text-gray-900 dark:text-white
            focus:ring-2 focus:ring-decisiv-blue/50 outline-hidden"
          aria-label="Default Claude model"
        >
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {(m.tierLabel || m.name) + (m.id === catalogDefault ? ' (Default)' : '')}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          New conversations will use this model. Existing conversations keep the model they were started with. Saved to your account.
        </p>
      </div>

      {/* Stream Responses */}
      <div className="flex items-center justify-between py-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Stream Responses</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Show responses as they are generated</p>
        </div>
        <button
          onClick={() => updateSetting('streamResponses', !settings.streamResponses)}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            settings.streamResponses ? 'bg-decisiv-blue' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              settings.streamResponses ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>
    </div>
  );
}

// Custom Instructions Tab Component
function InstructionsTab({ settings, updateSetting }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Custom Instructions</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Add instructions for DP to follow in all your conversations.
        </p>
      </div>

      <div>
        <label className="block font-medium text-gray-900 dark:text-white mb-2">
          What would you like DP to know about you?
        </label>
        <textarea
          value={settings.customInstructions}
          onChange={(e) => updateSetting('customInstructions', e.target.value)}
          className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600
            rounded-lg px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500
            resize-none focus:ring-2 focus:ring-decisiv-blue/50 outline-hidden"
          rows={8}
          placeholder="Example: I am a Service Advisor who deals with our customers every day. I like to be on top of trucks being repaired in my shop and always want quick status updates on repair orders..."
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {settings.customInstructions.length} / 2000 characters
        </p>
      </div>

      <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Tips for good instructions:</h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>- Describe your role (e.g. Service Advisor, Technician, Service Manager)</li>
          <li>- Mention your main focus areas (e.g. heavy trucks, quick lube, warranty repairs)</li>
          <li>- Describe your preferred response style (brief summaries vs step-by-step detail)</li>
          <li>- Include your communication preferences (formal vs casual, bullet points vs paragraphs)</li>
        </ul>
      </div>
    </div>
  );
}

// Privacy Tab Component
function PrivacyTab({ settings, updateSetting }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Privacy Settings</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Control how your data is stored and used.
        </p>
      </div>

      {/* Save History */}
      <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Save Conversation History</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Store conversations locally for future reference</p>
        </div>
        <button
          onClick={() => updateSetting('saveHistory', !settings.saveHistory)}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            settings.saveHistory ? 'bg-decisiv-blue' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              settings.saveHistory ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>

      {/* Analytics */}
      <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Share Usage Analytics</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Help improve the product with anonymous usage data</p>
        </div>
        <button
          onClick={() => updateSetting('shareAnalytics', !settings.shareAnalytics)}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            settings.shareAnalytics ? 'bg-decisiv-blue' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              settings.shareAnalytics ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>

      {/* Data Management */}
      <div className="pt-4 space-y-3">
        <h4 className="font-medium text-gray-900 dark:text-white">Data Management</h4>

        <button className="w-full text-left px-4 py-3 bg-gray-100 dark:bg-gray-700 rounded-lg
          hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Export All Data</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Download all your conversations and settings</p>
            </div>
            <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>
        </button>

        <button className="w-full text-left px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-lg
          hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors group border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-red-700 dark:text-red-400">Delete All Data</p>
              <p className="text-sm text-red-600/70 dark:text-red-400/70">Permanently remove all conversations and settings</p>
            </div>
            <svg className="w-5 h-5 text-red-400 group-hover:text-red-600 dark:group-hover:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
}

export default SettingsModal;
